import React, { useContext } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import { useStudent } from "../context/StudentContext";
import { MensajesContext } from "../context/MensajesContext";
import StudentHeader from "../components/StudentHeader";
import "../components/SidebarUser.css";
import { useAnnouncementsUnreadCount } from "../hooks/useAnnouncementsUnreadCount";
import { useStudentSidebarVisibility } from "../hooks/useStudentSidebarVisibility";
import StudentCourseSidebarNav from "../components/StudentCourseSidebarNav";
import StudentSidebarNavScroller from "../components/StudentSidebarNavScroller";
import { getMessage } from "../data/locales";

const StudentLayout = () => {
  const { courses, activeCourse, loading, showMessagingNav, userProfile } = useStudent();
  const canModifyCourses = userProfile?.permisos?.puedeModificar === true;
  const canCreateUsers = userProfile?.permisos?.puedeCrearUsuarios === true;
  const canViewRecords = userProfile?.permisos?.puedeVerRegistros === true;
  const canManageUsers =
    userProfile?.permisos?.puedeVerRegistros === true ||
    userProfile?.permisos?.puedeBorrarUsuarios === true ||
    userProfile?.permisos?.puedeBloquearUsuarios === true ||
    userProfile?.permisos?.puedeCambiarRoles === true;
  const { totalUnread } = useContext(MensajesContext);
  const { unreadCount: announcementsUnread } = useAnnouncementsUnreadCount(!loading && courses.length > 0);
  const location = useLocation();
  const { sidebarVisible, overlayMode, toggleSidebar, closeSidebar } = useStudentSidebarVisibility(
    `${location.pathname}${location.search}`,
  );

  if (loading) {
    return (
      <div className="student-loading-screen">
        <h2>Cargando tu espacio...</h2>
      </div>
    );
  }

  if (!courses || courses.length === 0) {
    return (
      <div className="side-containerPrinci">
        <StudentHeader />
        <main className="student-empty-courses">
          <section className="student-empty-courses__card" aria-labelledby="student-empty-courses-title">
            <div className="student-empty-courses__icon" aria-hidden="true">⌁</div>
            <h1 id="student-empty-courses-title" className="student-empty-courses__title">
              No tienes cursos activos
            </h1>
            <p className="student-empty-courses__description">
              Todavía no estás matriculado en ningún curso. Puedes consultar el catálogo
              y volver al campus cuando tengas uno asignado.
            </p>
            <div className="student-empty-courses__actions">
              <Link to="/cursos" className="student-empty-courses__primary">
                Ver cursos disponibles
              </Link>
              <Link to="/" className="student-empty-courses__secondary">
                Volver al inicio
              </Link>
            </div>
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className="side-containerPrinci side-containerPrinci--student">
      <StudentHeader
        showHamburger
        sidebarOpen={sidebarVisible}
        onToggleSidebar={toggleSidebar}
      />
      
      <div className="side-main-layout" style={{ position: 'relative' }}>
        {sidebarVisible && overlayMode ? (
          <button
            type="button"
            className="side-sidebar-backdrop"
            aria-label={getMessage("student.header.dismissMenu")}
            onClick={closeSidebar}
          />
        ) : null}
        <nav
          id="student-course-sidebar"
          className={`side-sidebar${sidebarVisible ? " visible" : " side-sidebar--hidden"}`}
          aria-label={getMessage("student.sidebar.navLabel")}
        >
          <h3 className="side-sidebar-title">
            {activeCourse
              ? activeCourse.titulo || activeCourse.nombre || getMessage("student.sidebar.menu")
              : getMessage("student.sidebar.userMenu")}
          </h3>
          <StudentSidebarNavScroller>
            <ul className="side-sidebar-nav-list" data-testid="student-sidebar-nav-list">
              {/* 1. Pestañas de base: Campus y Mensajes (siempre visibles) */}
              <li><Link to="/usuario/campus" className="side-sidebar-button">Campus</Link></li>
              <li>
                <Link to="/usuario/comunicados" className="side-sidebar-button side-sidebar-comunicados-link">
                  Comunicados
                  {announcementsUnread > 0 ? (
                    <span className="side-unread-badge" aria-label={`${announcementsUnread} comunicados sin leer`}>
                      {announcementsUnread > 99 ? "99+" : announcementsUnread}
                    </span>
                  ) : null}
                </Link>
              </li>
              {showMessagingNav ? (
                <li>
                  <Link
                    to={activeCourse?._id ? `/usuario/mensajeria-interna?curso=${activeCourse._id}` : "/usuario/mensajeria-interna"}
                    className="side-sidebar-button side-sidebar-messages-link"
                  >
                    Mensajes
                    {totalUnread > 0 ? (
                      <span className="side-unread-badge" aria-label={`${totalUnread} mensajes sin leer`}>
                        {totalUnread > 99 ? "99+" : totalUnread}
                      </span>
                    ) : null}
                  </Link>
                </li>
              ) : null}
              {canModifyCourses ? (
                <li><Link to="/GestionCursos" className="side-sidebar-button">Gestionar cursos</Link></li>
              ) : null}
              {canCreateUsers ? (
                <li><Link to="/Registrousuario" className="side-sidebar-button">Crear usuarios</Link></li>
              ) : null}
              {canManageUsers ? (
                <li><Link to="/centro-control-director" className="side-sidebar-button">Administrar usuarios</Link></li>
              ) : null}
              {canViewRecords ? (
                <li><Link to="/registro-actividades" className="side-sidebar-button">Ver registros</Link></li>
              ) : null}

              {/* 2. Pestañas dinámicas del curso activo (jerárquicas) */}
              {activeCourse && activeCourse.pestanasCurso && activeCourse.pestanasCurso.length > 0 ? (
                <StudentCourseSidebarNav />
              ) : (
                <>
                  <li><Link to="/usuario/resumenes" className="side-sidebar-button">Resúmenes</Link></li>
                  <li><Link to="/usuario/test-temas" className="side-sidebar-button">Test Temas</Link></li>
                  <li><Link to="/usuario/test-examen" className="side-sidebar-button">Test Examen</Link></li>
                </>
              )}
            </ul>
          </StudentSidebarNavScroller>
        </nav>

        <main className="side-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default StudentLayout;
